import { useEffect, useRef, useState, RefObject, useCallback } from 'react';
import { HubOption } from 'dorian-shared/types/branch/HubOption';

type UseScrollToNewOptionResult = {
  listRef: RefObject<HTMLDivElement>;
  newOptionUuid: string | null;
  trackBeforeAdd: () => void;
};

export function useScrollToNewOption(options: HubOption[]): UseScrollToNewOptionResult {
  const [newOptionUuid, setNewOptionUuid] = useState<string | null>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const snapshotRef = useRef<Set<string> | null>(null);
  const optionsRef = useRef(options);
  optionsRef.current = options;

  const trackBeforeAdd = useCallback(() => {
    snapshotRef.current = new Set(optionsRef.current.map((o) => o.uuid));
  }, []);

  useEffect(() => {
    // Only detect additions when trackBeforeAdd was explicitly called
    if (snapshotRef.current === null) return;

    const added = options.find((o) => !snapshotRef.current!.has(o.uuid));
    snapshotRef.current = null;

    if (added && listRef.current) {
      setNewOptionUuid(added.uuid);
      requestAnimationFrame(() => {
        const card = listRef.current?.querySelector<HTMLElement>(
          `[data-option-uuid="${added.uuid}"]`,
        );
        if (card) {
          card.scrollIntoView({ behavior: 'smooth', block: 'start' });
          card.classList.add('card-item--flash');
          card.addEventListener('animationend', () => {
            card.classList.remove('card-item--flash');
          }, { once: true });
        }
      });
    }
  }, [options]);

  return { listRef, newOptionUuid, trackBeforeAdd };
}
